import { faShelves } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  ButtonV2,
  ModalConfirmV2,
  ModalSizeVariants,
} from '@labrav/react-components'
import { Dispatch, SetStateAction } from 'react'
import { useDispatch, useSelector } from '../../../store'
import { Form, Formik, FormikProps } from 'formik'
import { OfferStakeholders } from '../../../types/flyout'
import { RecipientCheckBoxListItem } from '../RecipientCheckBoxListItem/RecipientCheckBoxListItem'
import { useStyles } from './OfferResendConfirmation.styles'
import { resendOffer } from '../../../modules/privateOffer/action'
import { noop } from 'lodash'
import { CloudMarketplace } from '../../../types'
import { LoadingTypes, isLoading as Loading } from '@labrav/common'

type OfferResendConfirmationItem = OfferStakeholders & {
  value: boolean
  title: string
}

type OfferResendConfirmationValuesType = {
  offerStakeholders: OfferResendConfirmationItem[]
}

export const OfferResendConfirmation = ({
  openOfferResendConfirmationModal,
  setOpenOfferResendConfirmationModal,
  offerStakeholders,
  offerId,
  handleSubmit,
  cloudMarketplace,
  partnerId,
}: {
  openOfferResendConfirmationModal: boolean
  setOpenOfferResendConfirmationModal: Dispatch<SetStateAction<boolean>>
  offerStakeholders?: OfferStakeholders[]
  offerId: string
  handleSubmit?: (data: Record<string, unknown>) => void
  cloudMarketplace: CloudMarketplace
  partnerId: string
}) => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const isLoading = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.UPDATE_FLYOUT_OFFER)
  )

  const handleCloseModal = () => {
    setOpenOfferResendConfirmationModal(false)
  }

  const handleConfirmModal = (values: OfferResendConfirmationValuesType) => {
    const offerRecipientsToSend = values?.offerStakeholders
      .filter(stakeholder => stakeholder?.value)
      .map(stakeholder => {
        const { name, email } = stakeholder
        return {
          name,
          email,
        }
      })
    if (handleSubmit) {
      handleSubmit({
        recipients: offerRecipientsToSend,
      })
    } else {
      dispatch(
        resendOffer({
          data: {
            recipients: offerRecipientsToSend,
          },
          privateOfferId: offerId,
          callBack: handleCloseModal,
          cloudMarketplace: cloudMarketplace,
          partnerId: partnerId,
        })
      )
    }
  }

  const initialValuesOfferResendConfirmation: OfferResendConfirmationValuesType =
    {
      offerStakeholders: (offerStakeholders || [])?.map(offerStakeholder => ({
        ...offerStakeholder,
        value: false,
        title: '',
      })),
    }

  return (
    <ModalConfirmV2
      title={'Resend offer confirmation'}
      open={openOfferResendConfirmationModal}
      acceptLoading={isLoading}
      onDecline={handleCloseModal}
      variant={ModalSizeVariants.large}
      content={
        <Formik
          initialValues={initialValuesOfferResendConfirmation}
          validateOnMount={true}
          onSubmit={value => {
            handleConfirmModal(value)
          }}
          enableReinitialize
        >
          {formikProps => {
            const handleSubmitForm = async () => {
              await formikProps?.submitForm()
            }
            const { values } = formikProps

            const isSendDisable = !values?.offerStakeholders?.filter(
              offerStakeholder => offerStakeholder?.value
            )?.length
            return (
              <Form>
                <div className={classes.selectRecipient}>Stakeholders</div>
                <div className={classes.root} data-testid={'offer-resend'}>
                  {values?.offerStakeholders?.map(
                    (offerStakeholderItem, index) => (
                      <RecipientCheckBoxListItem
                        key={`offerStakeholders[${index}].value`}
                        keyName={`offerStakeholders[${index}].value`}
                        hideBorder={
                          values?.offerStakeholders?.length - 1 === index
                        }
                        showName
                        recipient={offerStakeholderItem}
                        formik={
                          formikProps as unknown as FormikProps<
                            Record<string, unknown>
                          >
                        }
                      />
                    )
                  )}
                </div>
                <div className={classes.sectionText}>
                  You can add more stakeholders from right side panel on this
                  page.
                </div>
                <div className={classes.rowRightItem}>
                  <ButtonV2
                    data-testid="send-reminder"
                    smallVariant
                    styleType="outlined"
                    onClick={handleCloseModal}
                  >
                    Cancel
                  </ButtonV2>
                  <ButtonV2
                    data-testid="send-reminder"
                    smallVariant
                    styleType="primary"
                    onClick={handleSubmitForm}
                    loading={isLoading}
                    disabled={isSendDisable}
                  >
                    Resend
                  </ButtonV2>
                </div>
              </Form>
            )
          }}
        </Formik>
      }
      icon={
        <FontAwesomeIcon
          icon={faShelves}
          style={{ width: '14px', height: '14px' }}
        />
      }
      hideCancelIcon={false}
      hideCancelButton={true}
      hideAcceptButton={true}
      onAccept={noop}
    />
  )
}
